import React from 'react';
import isNil from 'lodash/isNil';
import {
	STATUS_FIELDTYPE,
	type JiraFieldType,
	NUMBER_FIELDTYPE,
	ASSIGNEE_FIELDTYPE,
	REPORTER_FIELDTYPE,
	CREATOR_FIELDTYPE,
	USERPICKER_FIELDTYPE,
	MULTI_USERPICKER_FIELDTYPE,
	WATCHERS_FIELDTYPE,
	PRIORITY_FIELDTYPE,
} from '../../../common/constants/fields';
import type {
	TableRefCurrent,
	MappingState,
	RowData,
	PriorityData,
	ColumnName,
	RowValue,
} from '../../../common/types';
import { AssigneeCell } from './assignee-cell';
import { ColumnHeader } from './column-header';
import { ColumnNameCell } from './column-name-cell';
import { NumberCell } from './number-cell';
import PriorityCell from './priority-cell';
import { StatusCell } from './status-cell';
import { TextCell } from './text-cell';

export const DEFAULT_COLUMN_WIDTH = 200;

export type ColumnWidths = { [columnKey: string]: number };

const getCellForRow = (
	key: string,
	priorities: PriorityData,
	value?: RowValue,
	fieldType?: JiraFieldType | null,
) => {
	if (isNil(value)) return null;

	switch (fieldType) {
		case STATUS_FIELDTYPE:
			return <StatusCell key={key} value={value} />;
		case NUMBER_FIELDTYPE:
			return <NumberCell key={key} value={value} />;
		case PRIORITY_FIELDTYPE:
			return <PriorityCell key={key} value={value} priorities={priorities} />;
		case ASSIGNEE_FIELDTYPE:
		case WATCHERS_FIELDTYPE:
		case REPORTER_FIELDTYPE:
		case CREATOR_FIELDTYPE:
		case USERPICKER_FIELDTYPE:
		case MULTI_USERPICKER_FIELDTYPE:
			return <AssigneeCell key={key} value={value} />;
		default:
			return <TextCell key={key} value={value} />;
	}
};

export const getTableColumns = (
	mappingState: MappingState | null,
	columnWidths: ColumnWidths,
	priorities: PriorityData,
) => {
	if (mappingState === null) {
		return [];
	}

	return mappingState.columnOrder
		.map((name) => mappingState.columns[name])
		.filter((col) => col.isVisible)
		.map((col, idx) => ({
			key: col.columnName,
			width: columnWidths[col.columnName] || DEFAULT_COLUMN_WIDTH,
			headerRenderer: (
				<ColumnHeader
					isFirstColumn={idx === 0}
					key={col.columnName}
					name={col.columnName}
					field={col.field}
				/>
			),
			cellRenderer: ({ rowData, rowIndex }: { rowData: RowData; rowIndex: number }) => {
				const key = `col-${col.columnName}-row-${rowIndex}`;

				if (rowIndex < 0) {
					return <ColumnNameCell key={key} value={col.columnName} />;
				}

				return getCellForRow(key, priorities, rowData[col.columnName], col.field?.fieldType);
			},
			frozen: false,
			maxWidth: 640,
			minWidth: 120,
			resizable: true,
		}));
};

type TargetColumnScrollPosition = {
	targetLeftEdgePosition: number;
	targetRightEdgePosition: number;
};

export const getTargetColumnScrollPosition = (
	mappingState: MappingState,
	columnWidths: ColumnWidths,
	selectedColumn: ColumnName,
): TargetColumnScrollPosition => {
	let targetLeftEdgePosition = 0;
	let targetRightEdgePosition = 0;
	for (const columnName of mappingState.columnOrder) {
		if (columnName === selectedColumn) {
			targetRightEdgePosition =
				targetLeftEdgePosition + (columnWidths[columnName] || DEFAULT_COLUMN_WIDTH);
			break;
		}
		if (mappingState.columns[columnName].isVisible) {
			targetLeftEdgePosition += columnWidths[columnName] || DEFAULT_COLUMN_WIDTH;
		}
	}

	return {
		targetLeftEdgePosition,
		targetRightEdgePosition,
	};
};

export const getNewScrollLeftPosition = (
	mappingState: MappingState,
	columnWidths: ColumnWidths,
	selectedColumn: ColumnName,
	tableRef: TableRefCurrent,
): number | undefined => {
	const tableWidth = tableRef?.table?.props?.width || 0;
	const scrollLeftPosition = tableRef?.table?.bodyRef?.state?.scrollLeft || 0;
	const { targetLeftEdgePosition, targetRightEdgePosition } = getTargetColumnScrollPosition(
		mappingState,
		columnWidths,
		selectedColumn,
	);

	if (targetLeftEdgePosition < scrollLeftPosition) {
		return targetLeftEdgePosition - tableWidth / 4;
	}

	if (targetRightEdgePosition > scrollLeftPosition + tableWidth) {
		return targetRightEdgePosition - (tableWidth * 3) / 4;
	}

	return undefined;
};
